import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import axios from "axios";
import Select from "react-select";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import logo from "../../images/logo.png";

// ✅ Reusable Yes/No toggle
const YesNoToggle = ({ name, value, onChange, label, disabled }) => {
  return (
    <div className="mb-3">
      <label className="d-block mb-1">{label}</label>
      <div className="btn-group" role="group">
        <input
          type="radio"
          className="btn-check"
          name={name}
          id={`${name}_yes`}
          value="Yes"
          checked={value === "Yes"}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
          required
        />
        <label
          className="btn btn-outline-primary"
          htmlFor={`${name}_yes`}
          style={{ minWidth: "250px" }}
        >
          Yes
        </label>

        <input
          type="radio"
          className="btn-check"
          name={name}
          id={`${name}_no`}
          value="No"
          checked={value === "No"}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
        />
        <label
          className="btn btn-outline-secondary"
          htmlFor={`${name}_no`}
          style={{ minWidth: "250px" }}
        >
          No
        </label>
      </div>
    </div>
  );
};

const FileUploader = ({ label, type, userId, disabled }) => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [files, setFiles] = React.useState([]);
  const token = localStorage.getItem("token");

  // ✅ useCallback ensures stable reference for useEffect dependency
  const fetchFiles = React.useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${BASE_URL}/api/qualifications/${userId}/uploads/${type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFiles(data || []);
    } catch (err) {
      console.error("❌ Failed to fetch files:", err);
    }
  }, [BASE_URL, userId, type, token]);

  // ✅ Run only when dependencies change (no lint warning)
  React.useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    if (!allowedTypes.includes(file.type)) {
      alert("Only PDF, JPG, or PNG files are allowed.");
      return;
    }

    const fd = new FormData();
    fd.append("file", file);

    try {
      await axios.post(
        `${BASE_URL}/api/qualifications/${userId}/upload/${type}`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await fetchFiles(); // refresh list
    } catch (err) {
      console.error("❌ Upload failed:", err);
      alert("Upload failed.");
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await axios.delete(
        `${BASE_URL}/api/qualifications/${userId}/uploads/${docId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchFiles(); // refresh list
    } catch (err) {
      console.error("❌ Delete failed:", err);
      alert("Delete failed.");
    }
  };

  return (
    <div className="mb-4">
      <label className="form-label">{label}</label>
      {!disabled && (
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="form-control mb-2"
          onChange={handleUpload}
        />
      )}

      <ul className="list-group">
        {files.map((f) => (
          <li
            key={f.id}
            className="list-group-item d-flex justify-content-between align-items-center"
          >
            <a
              href={`${BASE_URL}/uploads/prove_documents/${f.file}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {f.name}
            </a>
            {!disabled && (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => handleDelete(f.id)}
              >
                Delete
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

function QualificationsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const { id: paramId } = useParams();
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const [isSigned, setIsSigned] = useState(false);
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [eduOptions, setEduOptions] = useState([]);
  const [experienceOptions, setExperienceOptions] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/qualifications/${id}`);
        const driver = res.data?.driverQuestions || {};

        setForm((prev) => ({
          ...prev,
          emp_category: res.data.emp_category || "",
          driverQuestions: {
            ...driver,

            //40lb Question
            dq_lift_40lb: driver.dq_lift_40lb || "",

            // Question 1
            dq_previous_nwt_employee: driver.dq_previous_nwt_employee || "",
            dq_previous_nwt_from: driver.dq_previous_nwt_from
              ? dayjs(driver.dq_previous_nwt_from).format("YYYY-MM-DD")
              : "",
            dq_previous_nwt_to: driver.dq_previous_nwt_to
              ? dayjs(driver.dq_previous_nwt_to).format("YYYY-MM-DD")
              : "",

            // Question 2
            dq_conviction: driver.dq_conviction || "",
            dq_conviction_date: driver.dq_conviction_date || "",

            // Question 3
            dq_physical_condition: driver.dq_physical_condition || "",
            dq_physical_condition_detail:
              driver.dq_physical_condition_detail || "",

            // Question 4
            dq_fast_card_revoked: driver.dq_fast_card_revoked || "",
            dq_fast_card_revoked_date: driver.dq_fast_card_revoked_date || "",
            dq_fast_card_expiry: driver.dq_fast_card_expiry || "",

            // Question 5
            dq_accidents_last5yr: driver.dq_accidents_last5yr || "",
            dq_accidents_last5yr_date: driver.dq_accidents_last5yr_date || "",
            dq_accidents_last5yr_detail:
              driver.dq_accidents_last5yr_detail || "",

            // Question 6
            dq_violations_last3yr: driver.dq_violations_last3yr || "",
            dq_violations_last3yr_date: driver.dq_violations_last3yr_date || "",
            dq_violations_last3yr_detail:
              driver.dq_violations_last3yr_detail || "",

            // Question 7
            dq_training: driver.dq_training || "",
            dq_trainings: driver.dq_trainings || [],

            // Question 8
            dq_safe_driving_awards: driver.dq_safe_driving_awards || "",
            dq_awards: driver.dq_awards || [],

            // Question 9
            dq_license_denied: driver.dq_license_denied || "",
            dq_denied_explanation: driver.dq_denied_explanation || "",

            // Question 10
            dq_abstract_clean: driver.dq_abstract_clean || "",
            dq_abstract_explanation: driver.dq_abstract_explanation || "",

            // Question 11
            dq_license_suspended: driver.dq_license_suspended || "",
            dq_suspended_explanation: driver.dq_suspended_explanation || "",
          },
        }));
      } catch (err) {
        console.error("❌ Failed to fetch qualifications:", err);
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const TASK_ID = 20;
        const idKey = String(TASK_ID);
        const nameKey = "qualifications";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("❌ Failed to check signature status:", err);
      }
    };

    fetchData();
    fetchTaskStatus();
  }, [BASE_URL, id, userId, isAdmin]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [edu, exp, equip, regions] = await Promise.all([
        axios.get(`${BASE_URL}/api/dropdowns/highest-edu`, config),
        axios.get(`${BASE_URL}/api/dropdowns/exp_list`, config),
        axios.get(`${BASE_URL}/api/dropdowns/equip-operated`, config),
        axios.get(`${BASE_URL}/api/dropdowns/reg-travelled`, config),
      ]);
      setEduOptions(edu.data);
      setExperienceOptions(exp.data);
      setEquipmentOptions(equip.data);
      setRegionOptions(regions.data);
    };

    fetchDropdowns();
  }, [BASE_URL]);

  const isDriver = [2, 8, 11].includes(Number(form?.emp_category));

  const getLabelById = (list, id) => {
    const item = list.find(
      (el) => el.el_id === id || el.eo_id === id || el.rt_id === id
    );
    return item?.el_name || item?.eo_name || item?.rt_name || "";
  };

  const handleDriverChange = (key, value) => {
    setForm((prev) => {
      const updated = { ...prev.driverQuestions, [key]: value };

      // 🧹 Auto-clear dependent fields if switched to "No"
      const dependentMap = {
        dq_physical_condition: ["dq_physical_condition_detail"],
        dq_conviction: ["dq_conviction_date"],
        dq_fast_card_revoked: [
          "dq_fast_card_revoked_date",
          "dq_fast_card_revoked_detail",
        ],
        dq_accidents_last5yr: [
          "dq_accidents_last5yr_date",
          "dq_accidents_last5yr_detail",
        ],
        dq_violations_last3yr: [
          "dq_violations_last3yr_date",
          "dq_violations_last3yr_detail",
        ],
        dq_training: ["dq_trainings"],
        dq_safe_driving_awards: ["dq_awards"],
        dq_license_denied: ["dq_denied_explanation"],
        dq_license_suspended: ["dq_suspended_explanation"],
        dq_previous_nwt_employee: [
          "dq_previous_nwt_from",
          "dq_previous_nwt_to",
        ],
      };

      // 🧩 Separate behavior for dq_abstract_clean
      if (key === "dq_abstract_clean" && value === "Yes") {
        updated.dq_abstract_explanation = "";
      }

      if (value === "No" && dependentMap[key]) {
        dependentMap[key].forEach((dep) => {
          updated[dep] = Array.isArray(updated[dep]) ? [] : "";
        });
      }

      return { ...prev, driverQuestions: updated };
    });
  };

  const handleSubmit = async () => {
    try {
      const dq = form.driverQuestions || {};

      const payload = {
        emp_category: form.emp_category || "",
        driverQuestions: {
          // ✅ Include all toggle questions
          dq_legal_right_canada: dq.dq_legal_right_canada || "",
          dq_legal_right_us: dq.dq_legal_right_us || "",
          dq_fmcsa_registered: dq.dq_fmcsa_registered || "",
          dq_lift_40lb: dq.dq_lift_40lb || "",

          dq_previous_nwt_employee: dq.dq_previous_nwt_employee || "",
          dq_conviction: dq.dq_conviction || "",
          dq_physical_condition: dq.dq_physical_condition || "",
          dq_fast_card: dq.dq_fast_card || "",
          dq_fast_card_revoked: dq.dq_fast_card_revoked || "",
          dq_accidents_last5yr: dq.dq_accidents_last5yr || "",
          dq_violations_last3yr: dq.dq_violations_last3yr || "",
          dq_training: dq.dq_training || "",
          dq_safe_driving_awards: dq.dq_safe_driving_awards || "",
          dq_license_denied: dq.dq_license_denied || "",
          dq_abstract_clean: dq.dq_abstract_clean || "",
          dq_license_suspended: dq.dq_license_suspended || "",

          // ✅ Date fields (correct DB names)
          dq_previous_nwt_from: dq.dq_previous_nwt_from
            ? dayjs(dq.dq_previous_nwt_from).format("YYYY-MM-DD")
            : null,
          dq_previous_nwt_to: dq.dq_previous_nwt_to
            ? dayjs(dq.dq_previous_nwt_to).format("YYYY-MM-DD")
            : null,

          dq_conviction_date: dq.dq_conviction_date
            ? dayjs(dq.dq_conviction_date).format("YYYY-MM-DD")
            : null,

          dq_physical_condition_detail: dq.dq_physical_condition_detail || "",

          dq_fast_card_revoked_date: dq.dq_fast_card_revoked_date
            ? dayjs(dq.dq_fast_card_revoked_date).format("YYYY-MM-DD")
            : null,
          dq_fast_card_expiry: dq.dq_fast_card_expiry
            ? dayjs(dq.dq_fast_card_expiry).format("YYYY-MM-DD")
            : null,

          dq_accidents_last5yr_date: dq.dq_accidents_last5yr_date
            ? dayjs(dq.dq_accidents_last5yr_date).format("YYYY-MM-DD")
            : null,
          dq_accidents_last5yr_detail: dq.dq_accidents_last5yr_detail || "",

          dq_violations_last3yr_date: dq.dq_violations_last3yr_date
            ? dayjs(dq.dq_violations_last3yr_date).format("YYYY-MM-DD")
            : null,
          dq_violations_last3yr_detail: dq.dq_violations_last3yr_detail || "",

          dq_trainings: dq.dq_trainings || [],
          dq_awards: dq.dq_awards || [],

          dq_denied_explanation: dq.dq_denied_explanation || "",
          dq_abstract_explanation: dq.dq_abstract_explanation || "",
          dq_suspended_explanation: dq.dq_suspended_explanation || "",

          dq_commercial_license_date: dq.dq_commercial_license_date
            ? dayjs(dq.dq_commercial_license_date).format("YYYY-MM-DD")
            : null,

          // ✅ Education & Experience
          dq_education: dq.dq_education || "",
          dq_school: dq.dq_school || "",
          dq_experience: dq.dq_experience || [],
          dq_exp_detail: dq.dq_exp_detail || {},
          dq_reg_travelled: dq.dq_reg_travelled || [],
          dq_equip_operated: dq.dq_equip_operated || [],
          dq_equip_operated_detail: dq.dq_equip_operated_detail || {},
        },
      };

      await axios.post(`${BASE_URL}/api/qualifications/${id}`, payload);
      alert("✔️ Qualifications saved successfully!");
    } catch (err) {
      console.error("❌ Failed to save qualifications:", err);
      alert("❌ Failed to save qualifications.");
    }
  };

  const handleCancel = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  if (!form) return <div className="container mt-5">Loading...</div>;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Pg. 3 - Qualifications
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Pg. 3 - Qualifications</h2>
          </div>
          <CompanyAddress />
        </div>
        {/* ✅ Additional Questions */}
        <fieldset className="border p-3 mb-4">
          <legend className="w-auto px-2 mb-4">Additional Questions</legend>
          <div className="row">
            <div className="col-md-6 mb-3">
              <YesNoToggle
                name="dq_physical_condition"
                value={form.driverQuestions?.dq_physical_condition || ""}
                onChange={handleDriverChange}
                label="Do you have any physical condition which may limit your ability to perform the job applied for?"
                disabled={!isAdmin && isSigned}
              />
              {form.driverQuestions?.dq_physical_condition === "Yes" && (
                <div className="row mb-3">
                  <div className="col-md-12">
                    <label>Please provide details *</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={
                        form.driverQuestions?.dq_physical_condition_detail || ""
                      }
                      onChange={(e) =>
                        handleDriverChange(
                          "dq_physical_condition_detail",
                          e.target.value
                        )
                      }
                      disabled={!isAdmin && isSigned}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </fieldset>

        {isDriver && (
          <>
            {/* ✅ Personal Questions */}
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2 mb-4">Personal Questions</legend>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <YesNoToggle
                    name="dq_legal_right_canada"
                    value={form.driverQuestions?.dq_legal_right_canada || ""}
                    onChange={handleDriverChange}
                    label="Do you have the legal right to work in Canada?"
                    disabled={!isAdmin && isSigned}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <YesNoToggle
                    name="dq_legal_right_us"
                    value={form.driverQuestions?.dq_legal_right_us || ""}
                    onChange={handleDriverChange}
                    label="Do you have the legal right to enter the United States?"
                    disabled={!isAdmin && isSigned}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <YesNoToggle
                    name="dq_fmcsa_registered"
                    value={form.driverQuestions?.dq_fmcsa_registered || ""}
                    onChange={handleDriverChange}
                    label="Have you registered with the FMCSA Drug & Alcohol Clearinghouse?"
                    disabled={!isAdmin && isSigned}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <YesNoToggle
                    name="dq_lift_40lb"
                    value={form.driverQuestions?.dq_lift_40lb || ""}
                    onChange={handleDriverChange}
                    label="Are you able to lift above 40lb above the head?"
                    disabled={!isAdmin && isSigned}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <YesNoToggle
                    name="dq_previous_nwt_employee"
                    value={form.driverQuestions?.dq_previous_nwt_employee || ""}
                    onChange={handleDriverChange}
                    label="Have you ever worked for New Way Trucking Ltd before?"
                    disabled={!isAdmin && isSigned}
                  />
                  {form.driverQuestions?.dq_previous_nwt_employee === "Yes" && (
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label>From Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          value={
                            form.driverQuestions?.dq_previous_nwt_from || ""
                          }
                          onChange={(e) =>
                            handleDriverChange(
                              "dq_previous_nwt_from",
                              e.target.value
                            )
                          }
                          required
                          disabled={!isAdmin && isSigned}
                        />
                      </div>
                      <div className="col-md-6">
                        <label>To Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          value={form.driverQuestions?.dq_previous_nwt_to || ""}
                          onChange={(e) =>
                            handleDriverChange(
                              "dq_previous_nwt_to",
                              e.target.value
                            )
                          }
                          required
                          disabled={!isAdmin && isSigned}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </fieldset>

            {/* ✅ Qualifications */}
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2 mb-4">Qualifications</legend>
              <YesNoToggle
                name="dq_conviction"
                value={form.driverQuestions?.dq_conviction || ""}
                onChange={handleDriverChange}
                label="Have you ever been convicted of a crime for which a pardon has not been granted?"
                disabled={!isAdmin && isSigned}
              />
              {form.driverQuestions?.dq_conviction === "Yes" && (
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label>Date of Conviction</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.driverQuestions?.dq_conviction_date || ""}
                      onChange={(e) =>
                        handleDriverChange("dq_conviction_date", e.target.value)
                      }
                      disabled={!isAdmin && isSigned}
                    />
                  </div>
                </div>
              )}

              <YesNoToggle
                name="dq_fast_card"
                value={form.driverQuestions?.dq_fast_card || ""}
                onChange={handleDriverChange}
                label="FAST Card? "
                disabled={!isAdmin && isSigned}
              />
              {form.driverQuestions?.dq_fast_card === "Yes" && (
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label>FAST Card ID *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.driverQuestions?.dq_fast_card_no || ""}
                      onChange={(e) =>
                        handleDriverChange("dq_fast_card_no", e.target.value)
                      }
                      required
                      disabled={!isAdmin && isSigned}
                    />
                  </div>
                  <div className="col-md-4">
                    <label>FAST Card Expiry Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={
                        form.driverQuestions?.dq_fast_card_expiry
                          ? dayjs(
                              form.driverQuestions.dq_fast_card_expiry
                            ).format("YYYY-MM-DD")
                          : ""
                      }
                      onChange={(e) =>
                        handleDriverChange(
                          "dq_fast_card_expiry",
                          e.target.value
                        )
                      }
                      required
                      disabled={!isAdmin && isSigned}
                    />
                  </div>
                </div>
              )}

              <YesNoToggle
                name="dq_fast_card_revoked"
                value={form.driverQuestions?.dq_fast_card_revoked || ""}
                onChange={handleDriverChange}
                label="Have you ever had your FAST Card Revoked or Denied?"
                disabled={!isAdmin && isSigned}
              />
              {form.driverQuestions?.dq_fast_card_revoked === "Yes" && (
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label>Date of Revocation/Denial *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={
                        form.driverQuestions?.dq_fast_card_revoked_date || ""
                      }
                      onChange={(e) =>
                        handleDriverChange(
                          "dq_fast_card_revoked_date",
                          e.target.value
                        )
                      }
                      disabled={!isAdmin && isSigned}
                    />
                  </div>
                </div>
              )}
            </fieldset>

            {/* ✅ Accidents */}
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2 mb-4">
                Accidents / Incidents?
              </legend>
              <YesNoToggle
                name="dq_accidents_last5yr"
                value={form.driverQuestions?.dq_accidents_last5yr || ""}
                onChange={handleDriverChange}
                label="Do you have any accidents/incidents in the last 5 years (even if not at fault)?"
                disabled={!isAdmin && isSigned}
              />
              {form.driverQuestions?.dq_accidents_last5yr === "Yes" && (
                <>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label>Date of accidents/incidents *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={
                          form.driverQuestions?.dq_accidents_last5yr_date || ""
                        }
                        onChange={(e) =>
                          handleDriverChange(
                            "dq_accidents_last5yr_date",
                            e.target.value
                          )
                        }
                        disabled={!isAdmin && isSigned}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label>Please provide details *</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={
                          form.driverQuestions?.dq_accidents_last5yr_detail ||
                          ""
                        }
                        onChange={(e) =>
                          handleDriverChange(
                            "dq_accidents_last5yr_detail",
                            e.target.value
                          )
                        }
                        disabled={!isAdmin && isSigned}
                      />
                    </div>
                  </div>
                </>
              )}
            </fieldset>

            {/* ✅ Violations */}
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2 mb-4">
                Moving Violations / Traffic Convictions?
              </legend>
              <YesNoToggle
                name="dq_violations_last3yr"
                value={form.driverQuestions?.dq_violations_last3yr || ""}
                onChange={handleDriverChange}
                label="Do you have any moving violations / traffic convictions in the past 3 Years?"
                disabled={!isAdmin && isSigned}
              />
              {form.driverQuestions?.dq_violations_last3yr === "Yes" && (
                <>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label>
                        Date of moving violations / traffic convictions *
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={
                          form.driverQuestions?.dq_violations_last3yr_date || ""
                        }
                        onChange={(e) =>
                          handleDriverChange(
                            "dq_violations_last3yr_date",
                            e.target.value
                          )
                        }
                        disabled={!isAdmin && isSigned}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label>Please provide details *</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={
                          form.driverQuestions?.dq_violations_last3yr_detail ||
                          ""
                        }
                        onChange={(e) =>
                          handleDriverChange(
                            "dq_violations_last3yr_detail",
                            e.target.value
                          )
                        }
                        disabled={!isAdmin && isSigned}
                      />
                    </div>
                  </div>
                </>
              )}
            </fieldset>

            {/* ✅ Courses */}
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2 mb-4">Training Courses</legend>
              <YesNoToggle
                name="dq_training"
                value={form.driverQuestions?.dq_training || ""}
                onChange={handleDriverChange}
                label="Do you have any training courses related to this position?"
                disabled={!isAdmin && isSigned}
              />
              {form.driverQuestions?.dq_training === "Yes" && (
                <div className="mb-3">
                  {(form.driverQuestions?.dq_trainings || []).map(
                    (entry, index) => (
                      <div className="row mb-2" key={index}>
                        <div className="col-md-4">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type of Training"
                            value={entry.type || ""}
                            onChange={(e) => {
                              const updated = [
                                ...form.driverQuestions.dq_trainings,
                              ];
                              updated[index].type = e.target.value;
                              handleDriverChange("dq_trainings", updated);
                            }}
                            disabled={!isAdmin && isSigned}
                          />
                        </div>
                        <div className="col-md-4">
                          <input
                            type="date"
                            className="form-control"
                            value={entry.date || ""}
                            onChange={(e) => {
                              const updated = [
                                ...form.driverQuestions.dq_trainings,
                              ];
                              updated[index].date = e.target.value;
                              handleDriverChange("dq_trainings", updated);
                            }}
                            disabled={!isAdmin && isSigned}
                          />
                        </div>
                        <div className="col-md-2">
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => {
                              const updated = [
                                ...form.driverQuestions.dq_trainings,
                              ];
                              updated.splice(index, 1);
                              handleDriverChange("dq_trainings", updated);
                            }}
                            disabled={!isAdmin && isSigned}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    )
                  )}
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          const updated = [
                            ...(form.driverQuestions.dq_trainings || []),
                          ];
                          updated.push({ type: "", date: "" });
                          handleDriverChange("dq_trainings", updated);
                        }}
                        disabled={!isAdmin && isSigned}
                      >
                        Add Training
                      </button>
                    </div>
                  </div>
                  <FileUploader
                    label="Upload Training Certificates (PDF / JPG / PNG)"
                    type="training"
                    userId={userId}
                    disabled={!isAdmin && isSigned}
                  />
                </div>
              )}
            </fieldset>

            {/* ✅ Awards */}
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2 mb-4">Driving Awards</legend>
              <YesNoToggle
                name="dq_safe_driving_awards"
                value={form.driverQuestions?.dq_safe_driving_awards || ""}
                onChange={handleDriverChange}
                label="Do you have any safe driving awards?"
                disabled={!isAdmin && isSigned}
              />
              {form.driverQuestions?.dq_safe_driving_awards === "Yes" && (
                <div className="mb-3">
                  {(form.driverQuestions?.dq_awards || []).map(
                    (award, index) => (
                      <div className="row mb-2" key={index}>
                        <div className="col-md-4">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type of Award"
                            value={award.type || ""}
                            onChange={(e) => {
                              const updated = [
                                ...form.driverQuestions.dq_awards,
                              ];
                              updated[index].type = e.target.value;
                              handleDriverChange("dq_awards", updated);
                            }}
                            disabled={!isAdmin && isSigned}
                          />
                        </div>
                        <div className="col-md-4">
                          <input
                            type="date"
                            className="form-control"
                            value={award.date || ""}
                            onChange={(e) => {
                              const updated = [
                                ...form.driverQuestions.dq_awards,
                              ];
                              updated[index].date = e.target.value;
                              handleDriverChange("dq_awards", updated);
                            }}
                            disabled={!isAdmin && isSigned}
                          />
                        </div>
                        <div className="col-md-2">
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => {
                              const updated = [
                                ...form.driverQuestions.dq_awards,
                              ];
                              updated.splice(index, 1);
                              handleDriverChange("dq_awards", updated);
                            }}
                            disabled={!isAdmin && isSigned}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    )
                  )}
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          const updated = [
                            ...(form.driverQuestions.dq_awards || []),
                          ];
                          updated.push({ type: "", date: "" });
                          handleDriverChange("dq_awards", updated);
                        }}
                        disabled={!isAdmin && isSigned}
                      >
                        Add Award
                      </button>
                    </div>
                  </div>
                  <FileUploader
                    label="Upload Driving Award Certificates (PDF)"
                    type="award"
                    userId={userId}
                    disabled={!isAdmin && isSigned}
                  />
                </div>
              )}
            </fieldset>

            {/* ✅ Driver Licence Questions */}
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2 mb-4">
                Driver Licence Questions
              </legend>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>
                    When did you obtain your Commercial Driver’s Licence or plan
                    to do so? *
                  </label>
                  <div className="col-md-7">
                    <input
                      type="date"
                      className="form-control"
                      value={
                        form.driverQuestions?.dq_commercial_license_date
                          ? dayjs(
                              form.driverQuestions.dq_commercial_license_date
                            ).format("YYYY-MM-DD")
                          : ""
                      }
                      onChange={(e) =>
                        handleDriverChange(
                          "dq_commercial_license_date",
                          e.target.value
                        )
                      }
                      disabled={!isAdmin && isSigned}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <YesNoToggle
                    name="dq_license_denied"
                    value={form.driverQuestions?.dq_license_denied || ""}
                    onChange={handleDriverChange}
                    label="Have you ever been denied a licence, permit or privilege to operate a motor vehicle?"
                    disabled={!isAdmin && isSigned}
                  />
                  {form.driverQuestions?.dq_license_denied === "Yes" && (
                    <div className="row mb-3">
                      <div className="col-md-12">
                        <label>Please explain *</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={
                            form.driverQuestions?.dq_denied_explanation || ""
                          }
                          onChange={(e) =>
                            handleDriverChange(
                              "dq_denied_explanation",
                              e.target.value
                            )
                          }
                          disabled={!isAdmin && isSigned}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <YesNoToggle
                    name="dq_abstract_clean"
                    value={form.driverQuestions?.dq_abstract_clean || ""}
                    onChange={handleDriverChange}
                    label="Do you have a clean abstract and driving record?"
                    disabled={!isAdmin && isSigned}
                  />
                  {form.driverQuestions?.dq_abstract_clean === "No" && (
                    <div className="row mb-3">
                      <div className="col-md-12">
                        <label>Please explain *</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={
                            form.driverQuestions?.dq_abstract_explanation || ""
                          }
                          onChange={(e) =>
                            handleDriverChange(
                              "dq_abstract_explanation",
                              e.target.value
                            )
                          }
                          disabled={!isAdmin && isSigned}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <YesNoToggle
                    name="dq_license_suspended"
                    value={form.driverQuestions?.dq_license_suspended || ""}
                    onChange={handleDriverChange}
                    label="Has any licence, permit or privilege ever been suspended or revoked?"
                    disabled={!isAdmin && isSigned}
                  />
                  {form.driverQuestions?.dq_license_suspended === "Yes" && (
                    <div className="row mb-3">
                      <div className="col-md-12">
                        <label>Please explain *</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={
                            form.driverQuestions?.dq_suspended_explanation || ""
                          }
                          onChange={(e) =>
                            handleDriverChange(
                              "dq_suspended_explanation",
                              e.target.value
                            )
                          }
                          disabled={!isAdmin && isSigned}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </fieldset>
          </>
        )}

        {/* ✅ Final Qualification Questions (education, etc.) */}
        <fieldset className="border p-3 mb-4">
          <legend className="w-auto px-2 mb-4">
            Final Qualification Questions
          </legend>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label>Select Highest Education</label>
              <select
                className="form-select"
                value={form.driverQuestions?.dq_education || ""}
                onChange={(e) =>
                  handleDriverChange("dq_education", Number(e.target.value))
                }
                disabled={!isAdmin && isSigned}
              >
                <option value="">Select</option>
                {eduOptions.map((edu) => (
                  <option key={edu.he_id} value={edu.he_id}>
                    {edu.he_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label>Last School Attended</label>
              <input
                type="text"
                className="form-control"
                value={form.driverQuestions?.dq_school || ""}
                onChange={(e) =>
                  handleDriverChange("dq_school", e.target.value)
                }
                disabled={!isAdmin && isSigned}
              />
            </div>
          </div>

          {/* Normal User: Experience Section */}
          {!isDriver && (
            <>
              <label>Experience</label>
              <div className="mb-3">
                <Select
                  isMulti
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={experienceOptions
                    .filter((opt) =>
                      form.driverQuestions?.dq_experience?.includes(opt.el_id)
                    )
                    .map((opt) => ({
                      value: opt.el_id,
                      label: opt.el_name,
                    }))}
                  options={experienceOptions.map((opt) => ({
                    value: opt.el_id,
                    label: opt.el_name,
                  }))}
                  onChange={(selected) => {
                    const selectedIds = selected.map((s) => s.value);
                    handleDriverChange("dq_experience", selectedIds);
                  }}
                  isDisabled={!isAdmin && isSigned}
                />
              </div>

              {/* Experience Detail Section */}
              {form.driverQuestions?.dq_experience?.map((el_id) => (
                <div
                  key={el_id}
                  className="row mb-2 p-3 border rounded bg-light"
                >
                  <strong className="mb-2">
                    {getLabelById(experienceOptions, el_id)}
                  </strong>
                  <div className="col-md-3">
                    <label>Years Exp.</label>
                    <input
                      type="number"
                      className="form-control"
                      value={
                        form.driverQuestions?.dq_exp_detail?.[el_id]?.years ||
                        ""
                      }
                      onChange={(e) =>
                        handleDriverChange("dq_exp_detail", {
                          ...(form.driverQuestions?.dq_exp_detail || {}),
                          [el_id]: {
                            ...(form.driverQuestions?.dq_exp_detail?.[el_id] ||
                              {}),
                            years: e.target.value,
                          },
                        })
                      }
                      disabled={!isAdmin && isSigned}
                    />
                  </div>
                  <div className="col-md-9">
                    <label>Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        form.driverQuestions?.dq_exp_detail?.[el_id]?.desc || ""
                      }
                      onChange={(e) =>
                        handleDriverChange("dq_exp_detail", {
                          ...(form.driverQuestions?.dq_exp_detail || {}),
                          [el_id]: {
                            ...(form.driverQuestions?.dq_exp_detail?.[el_id] ||
                              {}),
                            desc: e.target.value,
                          },
                        })
                      }
                      disabled={!isAdmin && isSigned}
                    />
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Driver: Regions + Equipment */}
          {isDriver && (
            <>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Regions Travelled</label>

                  <Select
                    isMulti
                    className="basic-multi-select"
                    classNamePrefix="select"
                    value={regionOptions
                      .filter((opt) =>
                        form.driverQuestions?.dq_reg_travelled?.includes(
                          opt.rt_id
                        )
                      )
                      .map((opt) => ({
                        value: opt.rt_id,
                        label: opt.rt_name,
                      }))}
                    options={regionOptions.map((opt) => ({
                      value: opt.rt_id,
                      label: opt.rt_name,
                    }))}
                    onChange={(selected) => {
                      const selectedIds = selected.map((s) => s.value);
                      handleDriverChange("dq_reg_travelled", selectedIds);
                    }}
                    isDisabled={!isAdmin && isSigned}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Equipment Operated</label>

                  <Select
                    isMulti
                    className="basic-multi-select"
                    classNamePrefix="select"
                    value={equipmentOptions
                      .filter((opt) =>
                        form.driverQuestions?.dq_equip_operated?.includes(
                          opt.eo_id
                        )
                      )
                      .map((opt) => ({
                        value: opt.eo_id,
                        label: opt.eo_name,
                      }))}
                    options={equipmentOptions.map((opt) => ({
                      value: opt.eo_id,
                      label: opt.eo_name,
                    }))}
                    onChange={(selected) => {
                      const selectedIds = selected.map((s) => s.value);
                      handleDriverChange("dq_equip_operated", selectedIds);
                    }}
                    isDisabled={!isAdmin && isSigned}
                  />
                </div>
              </div>

              {form.driverQuestions?.dq_equip_operated?.map((eo_id) => (
                <div
                  key={eo_id}
                  className="row mb-2 p-3 border rounded bg-light"
                >
                  <strong className="mb-2">
                    {getLabelById(equipmentOptions, eo_id)}
                  </strong>
                  <div className="col-md-3">
                    <label># of Miles</label>
                    <input
                      type="number"
                      className="form-control"
                      value={
                        form.driverQuestions?.dq_equip_operated_detail?.[eo_id]
                          ?.miles || ""
                      }
                      onChange={(e) =>
                        handleDriverChange("dq_equip_operated_detail", {
                          ...(form.driverQuestions?.dq_equip_operated_detail ||
                            {}),
                          [eo_id]: {
                            ...(form.driverQuestions
                              ?.dq_equip_operated_detail?.[eo_id] || {}),
                            miles: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="col-md-9">
                    <label>Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        form.driverQuestions?.dq_equip_operated_detail?.[eo_id]
                          ?.desc || ""
                      }
                      onChange={(e) =>
                        handleDriverChange("dq_equip_operated_detail", {
                          ...(form.driverQuestions?.dq_equip_operated_detail ||
                            {}),
                          [eo_id]: {
                            ...(form.driverQuestions
                              ?.dq_equip_operated_detail?.[eo_id] || {}),
                            desc: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              ))}
            </>
          )}
        </fieldset>

        <div className="d-flex justify-content-end">
          <button
            className="btn btn-success px-5"
            onClick={handleSubmit}
            disabled={!isAdmin && isSigned}
          >
            Submit
          </button>
        </div>
      </div>

      {userId && (
        <>
          <SignatureManager
            taskName="qualifications"
            taskId={20}
            userId={isAdmin && paramId ? paramId : userId}
            isAdmin={isAdmin}
          />
          <ReviewManager
            taskName="qualifications"
            taskId={20}
            userId={isAdmin && paramId ? paramId : userId}
            isAdmin={isAdmin}
          />
        </>
      )}
    </>
  );
}

export default QualificationsPage;
